/**
 * Inbox Triage Workflow
 *
 * Periodically scans the inbox and prioritizes unread emails:
 * 1. Fetches unread emails
 * 2. Classifies each by urgency and relevance
 * 3. Groups by category (work, personal, newsletter, spam)
 * 4. Sends a summary notification with action suggestions
 */

import type { Workflow } from './types';

export const inboxTriageWorkflow: Workflow = {
  id: 'inbox-triage',
  name: 'Triage Inbox',
  description: 'Analizza le email non lette, le classifica per urgenza e genera un riepilogo con azioni suggerite',
  trigger: {
    type: 'schedule',
    config: {
      cron: '0 8,13,17 * * *', // Three times daily: 8 AM, 1 PM, 5 PM
      timezone: 'Europe/Rome',
    },
  },
  steps: [
    {
      id: 'fetch_unread',
      name: 'Recupera Email Non Lette',
      type: 'custom',
      config: {
        graphqlQuery: 'emails',
        filters: { read: false, archived: false },
        limit: 50,
        sortBy: 'receivedAt',
        sortOrder: 'desc',
      },
      next: ['classify_batch'],
    },
    {
      id: 'classify_batch',
      name: 'Classifica per Urgenza',
      type: 'ai_analyze',
      config: {
        prompt: `Analyze these unread emails and classify each by urgency.
For each email, determine:
- urgency: "high" (needs immediate response), "medium" (respond today), "low" (can wait)
- category: "work", "personal", "newsletter", "spam"
- suggestedAction: brief action suggestion

Return JSON: { "emails": [{ "id": "...", "urgency": "...", "category": "...", "suggestedAction": "..." }] }`,
        inputField: 'unreadEmails',
      },
      next: ['generate_summary'],
      onError: 'continue',
    },
    {
      id: 'generate_summary',
      name: 'Genera Riepilogo',
      type: 'ai_summarize',
      config: {
        prompt: `Generate a concise inbox triage summary in Italian.
Group emails by urgency (high first), show count per category.
Include top 3 suggested actions.`,
        maxLength: 300,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Notifica Riepilogo Inbox',
      type: 'send_notification',
      config: {
        type: 'in_app',
        icon: 'mail-triage',
        priority: 'normal',
        message: '{emailCount} email non lette: {highCount} urgenti, {mediumCount} da leggere oggi.',
        actions: ['open_inbox', 'dismiss'],
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};
